import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { RawTexture } from "@babylonjs/core/Materials/Textures/rawTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { createGrid, generateTerrain, sampleElevation, sampleField, type Grid } from "../sim/grid";
import { fbm } from "../sim/noise";
import { createWaterMaterial } from "./waterMaterial";

/** Everything Phase 2 needs to move over the island: the meshes + the shared grid. */
export interface Terrain {
  grid: Grid;
  mesh: Mesh;
  water: Mesh;
  /** Ground height at a world point. */
  heightAt(x: number, z: number): number;
  /** Valid teleport/plant target: on land, above water, inside the island. */
  isValidTarget(x: number, z: number): boolean;
}

const LOWPOLY_RES = 132; // terrain tessellation; faceted for the low-poly look

/**
 * Build the island from the Phase 1 heightmap. The sim `Grid` is generated with
 * the same seed used by the ecology, so terrain and simulation agree. Low-poly,
 * flat-shaded, vertex-coloured — reads better unlit and is nearly free.
 */
export function createTerrain(scene: Scene, seed: number, worldSize: number, waterLevel: number): Terrain {
  const grid = createGrid(128, worldSize, waterLevel);
  generateTerrain(grid, seed);

  const mesh = buildTerrainMesh(scene, grid);
  const water = buildWater(scene, grid, worldSize, waterLevel);

  const half = worldSize * 0.5;
  const heightAt = (x: number, z: number): number => sampleElevation(grid, x, z);
  const isValidTarget = (x: number, z: number): boolean => {
    if (x < -half || x > half || z < -half || z > half) return false;
    // Land iff the (bilinear) elevation is above the water surface, with a small
    // margin so the very shoreline doesn't teleport you ankle-deep.
    return sampleElevation(grid, x, z) > waterLevel + 0.12 && sampleField(grid, grid.land, x, z) > 0.5;
  };

  return { grid, mesh, water, heightAt, isValidTarget };
}

function buildTerrainMesh(scene: Scene, grid: Grid): Mesh {
  const res = LOWPOLY_RES;
  const half = grid.worldSize * 0.5;
  const step = grid.worldSize / res;

  // Non-indexed geometry: every quad gets its own vertices so normals are
  // per-face → a faceted, low-poly surface.
  const positions: number[] = [];
  const colors: number[] = [];

  const vx = (i: number): number => -half + i * step;
  const h = (x: number, z: number): number => Math.max(sampleElevation(grid, x, z), grid.waterLevel - 0.4);

  const pushVert = (x: number, z: number): void => {
    const y = h(x, z);
    positions.push(x, y, z);
    // Slope from the local elevation gradient (steep faces read as rock/scree).
    const e = step * 0.9;
    const dzdx = (sampleElevation(grid, x + e, z) - sampleElevation(grid, x - e, z)) / (2 * e);
    const dzdz = (sampleElevation(grid, x, z + e) - sampleElevation(grid, x, z - e)) / (2 * e);
    const slope = Math.hypot(dzdx, dzdz);
    // Two-octave mottle so flat swathes aren't a single flat colour.
    const mottle = fbm(x * 0.06 + 3.1, z * 0.06 + 1.7, 1337, 3, 2.0);
    const [r, g, b] = groundColor(y, sampleField(grid, grid.moisture, x, z), slope, mottle, grid.waterLevel);
    colors.push(r, g, b, 1);
  };

  for (let iz = 0; iz < res; iz++) {
    for (let ix = 0; ix < res; ix++) {
      const x0 = vx(ix);
      const x1 = vx(ix + 1);
      const z0 = vx(iz);
      const z1 = vx(iz + 1);
      // two triangles, wound so face normals point up (lit by the overhead sun)
      pushVert(x0, z0);
      pushVert(x1, z1);
      pushVert(x0, z1);
      pushVert(x0, z0);
      pushVert(x1, z0);
      pushVert(x1, z1);
    }
  }

  // Non-indexed: identity index buffer, one triangle per 3 consecutive verts.
  const indices: number[] = [];
  for (let i = 0; i < positions.length / 3; i++) indices.push(i);
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  const data = new VertexData();
  data.positions = positions;
  data.indices = indices;
  data.normals = normals;
  data.colors = colors;

  const mesh = new Mesh("terrain", scene);
  data.applyToMesh(mesh);

  const mat = new StandardMaterial("terrainMat", scene);
  mat.diffuseColor = new Color3(1, 1, 1);
  mat.specularColor = new Color3(0, 0, 0); // matte ground, no highlights
  mesh.material = mat;
  mesh.useVertexColors = true; // consume the per-vertex colour buffer
  mesh.receiveShadows = false;
  return mesh;
}

type RGB = [number, number, number];
function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
function mixRGB(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

// A green-dominant palette: the land stays lush; moisture, a soft mottle and a
// little slope shading add variation without turning it into sand and rock.
const C_DRY_GREEN: RGB = [0.40, 0.55, 0.25]; // drier upland grass, warm green
const C_WET_GREEN: RGB = [0.17, 0.44, 0.21]; // wet lowland turf, deep green
const C_EARTH: RGB = [0.34, 0.40, 0.22]; // steep faces — earthy, not grey rock
const C_SHORE: RGB = [0.55, 0.60, 0.40]; // soft sandy-green at the water line

/**
 * Ground colour: lush green everywhere, shading deeper in the wet hollows and a
 * touch warmer/lighter on dry high ground, with a gentle mottle for life and a
 * subtle earthy tint on steep faces and a soft band along the shore.
 */
function groundColor(y: number, moisture: number, slope: number, mottle: number, waterLevel: number): RGB {
  const a = y - waterLevel; // height above the water surface (m)

  const m = smoothstep(0.15, 0.8, moisture); // wetness
  let col = mixRGB(C_DRY_GREEN, C_WET_GREEN, m);

  // soft mottle so large flat areas aren't a single flat green
  const mo = (mottle - 0.5) * 0.09;
  col = [col[0] + mo, col[1] + mo * 1.1, col[2] + mo * 0.7];

  // gentle earthy shading on the steepest faces (subtle)
  col = mixRGB(col, C_EARTH, smoothstep(0.9, 1.7, slope) * 0.35);

  // soft shore tint hugging the water line
  col = mixRGB(col, C_SHORE, smoothstep(0.7, 0.05, a) * 0.5);

  return col;
}

const ELEV_SCALE = 8.0; // heightmap byte 0..255 → elevation 0..ELEV_SCALE metres

function buildWater(scene: Scene, grid: Grid, worldSize: number, waterLevel: number): Mesh {
  // Extend the water far past the island so its edge falls below the horizon —
  // otherwise you can see the world boundary as a straight line out at sea. Flat
  // 2-triangle plane, so a huge span is effectively free.
  const span = Math.max(worldSize * 40, 2000);
  const water = CreateGround("water", { width: span, height: span, subdivisions: 1 }, scene);
  water.position.y = waterLevel;

  // Bake the elevation into a texture so the water shader knows the depth (and
  // thus the shoreline) at every point.
  const size = grid.size;
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const v = Math.max(0, Math.min(255, Math.round((grid.elevation[i]! / ELEV_SCALE) * 255)));
    data[i * 4] = v;
    data[i * 4 + 1] = v;
    data[i * 4 + 2] = v;
    data[i * 4 + 3] = 255;
  }
  const heightMap = RawTexture.CreateRGBATexture(data, size, size, scene, false, false, Texture.BILINEAR_SAMPLINGMODE);
  heightMap.wrapU = Texture.CLAMP_ADDRESSMODE;
  heightMap.wrapV = Texture.CLAMP_ADDRESSMODE;

  const mat = createWaterMaterial(scene, heightMap, worldSize, waterLevel, ELEV_SCALE);
  water.material = mat;
  water.isPickable = false;
  water.applyFog = false;

  // Gentle swell: the whole surface eases up and down a few centimetres. The
  // shader's waterLevel tracks it, so the foam edge advances and recedes over the
  // shallow shore — water lapping the coast.
  scene.onBeforeRenderObservable.add(() => {
    const t = performance.now() / 1000;
    const level = waterLevel + Math.sin(t * 0.5) * 0.05 + Math.sin(t * 0.23) * 0.03;
    water.position.y = level;
    mat.setFloat("waterLevel", level);
  });
  return water;
}
